/** The API error envelope (see `ExceptionResponse` in apps/api). */
export interface ApiErrorBody {
  statusCode?: number;
  code?: string;
  message?: string;
  errors?: { field: string; message: string; code?: string }[];
}

/** Every failure — network, HTTP, unexpected body — reaches the UI as this type. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody = {}) {
    super(body.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** 401/403 are neither retried nor shown as a generic failure. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** 400/422 go back to the form, not to a toast. */
  get isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/**
 * The API refuses every call but the change itself while the user still holds a temporary
 * password (`403 PASSWORD_CHANGE_REQUIRED`).
 */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';

export function isPasswordChangeRequired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.body.code === PASSWORD_CHANGE_REQUIRED
  );
}

/**
 * The organization's demo period ended: the API refuses every call but the own session
 * (`403 DEMO_EXPIRED`) until the platform extends it or changes the plan.
 */
export const DEMO_EXPIRED = 'DEMO_EXPIRED';

export function isDemoExpired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.body.code === DEMO_EXPIRED;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The message to show, in the reader's language. The API answers with a CODE; the UI
 * translates `errors.api.<CODE>`. A code without a translation falls back to the generic
 * message — never to the raw key.
 */
export function apiErrorMessage(
  cause: unknown,
  translate: Translate,
  fallbackKey = 'errors.generic',
): string {
  const code = cause instanceof ApiError ? cause.body.code : undefined;
  if (code) {
    const key = `errors.api.${code}`;
    const translated = translate(key);
    if (translated !== key) return translated;
  }
  return translate(fallbackKey);
}
